import React from 'react';

export type ButtonVariant =
  /** The one action a card or modal exists for. At most one per surface. */
  | 'primary'
  /** Any other action: Cancel, Export, Settings. */
  | 'secondary'
  /** A filter or toggle chip on a toolbar. Pass `pressed` for its on state. */
  | 'soft'
  /** A text-only action that should not compete: "Clear", "Skip". */
  | 'ghost'
  /** Confirms something irreversible: delete, remove, purge. */
  | 'danger'
  /** Offers something destructive without urging it. */
  | 'danger-outline'
  /** Inline, in the flow of text: "See transactions". */
  | 'link';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  /** On state for a toggle. Renders `aria-pressed`, so screen readers hear it too. */
  pressed?: boolean;
}

/**
 * Every button in the app. Its look lives in `statement.css` under `.st-btn`
 * so hover, focus and disabled states work, which inline styles cannot do.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'secondary', size = 'md', pressed, className, type = 'button', ...rest }, ref) => (
    <button
      ref={ref}
      type={type}
      className={`st-btn${className ? ` ${className}` : ''}`}
      data-variant={variant}
      data-size={size}
      aria-pressed={pressed}
      {...rest}
    />
  ),
);
Button.displayName = 'Button';

/** The ✕ in a panel or modal header. */
export const CloseButton: React.FC<{ onClick: () => void; label?: string }> = ({ onClick, label = 'Close' }) => (
  <button type="button" className="st-btn st-close" data-variant="soft" data-size="sm" aria-label={label} onClick={onClick}>
    ✕
  </button>
);
